import type { RoleDto } from "../../services/UserService";
import type { Role, RoleScope, SystemRoleType } from "../../state/role";
import RoleId from "../../state/RoleId";
import type { TranslationKey } from "../../state/i18n";
import { isEmpty, uniq } from "lodash-es";
import { keyFromSelector } from "i18next";
import { extractDateFromObjectId } from "../objectIdUtil";
import type { PermissionType } from "../../state/Permission";
import { PERMISSION_DEFINITIONS_BY_TYPE, PERMISSION_TYPES } from "../../state/Permission";

const resolveScope = (permissions: PermissionType[]): RoleScope => {
  if (isEmpty(permissions)) {
    // There are no permissions we can resolve scope from, so default to "CUSTOMER"
    return "CUSTOMER";
  }

  const uniqueScopes = uniq(permissions.map((permissionType) => PERMISSION_DEFINITIONS_BY_TYPE[permissionType].scope));
  return uniqueScopes.length === 1 ? (uniqueScopes[0] as RoleScope) : "MIXED";
};

const resolveType = (translatableKey: string): SystemRoleType => {
  switch (translatableKey) {
    case "ROLE_ACCOUNT_OWNER":
      return "ACCOUNT_OWNER";
    case "ROLE_ACCOUNT_ADMINISTRATOR":
      return "ACCOUNT_ADMINISTRATOR";
    case "ROLE_USER":
      return "USER";
    case "ROLE_FOLDER_MANAGER":
      return "FOLDER_MANAGER";
    case "ROLE_FORM_MANAGER":
      return "FORM_MANAGER";
    case "ROLE_FORM_USER":
      return "FORM_USER";
    default:
      throw new Error("Unknown system role: " + translatableKey);
  }
};

export const toRole = (dto: RoleDto): Role => {
  const permissions = dto.permissions.filter((permission): permission is PermissionType =>
    PERMISSION_TYPES.includes(permission),
  );

  const system = dto.readOnly;
  return {
    id: RoleId.from(dto.id),
    permissions,
    scope: resolveScope(permissions),
    ...(system
      ? {
          system: true,
          type: resolveType(dto.translatableKey as TranslationKey),
          name: SYSTEM_ROLE_NAME_TRANSLATION_KEYS[resolveType(dto.translatableKey as TranslationKey)],
          description: SYSTEM_ROLE_DESCRIPTION_TRANSLATION_KEYS[resolveType(dto.translatableKey as TranslationKey)],
        }
      : {
          system: false,
          name: dto.name,
          description: dto.description,
          createdOn: extractDateFromObjectId(dto.id),
        }),
  };
};

const SYSTEM_ROLE_NAME_TRANSLATION_KEYS: Record<SystemRoleType, TranslationKey> = {
  ACCOUNT_OWNER: keyFromSelector(($) => $.ROLE_SYSTEM_ACCOUNT_OWNER_NAME),
  ACCOUNT_ADMINISTRATOR: keyFromSelector(($) => $.ROLE_SYSTEM_ACCOUNT_ADMINISTRATOR_NAME),
  USER: keyFromSelector(($) => $.ROLE_SYSTEM_USER_NAME),
  FOLDER_MANAGER: keyFromSelector(($) => $.ROLE_SYSTEM_FOLDER_MANAGER_NAME),
  FORM_MANAGER: keyFromSelector(($) => $.ROLE_SYSTEM_FORM_MANAGER_NAME),
  FORM_USER: keyFromSelector(($) => $.ROLE_SYSTEM_FORM_USER_NAME),
};

const SYSTEM_ROLE_DESCRIPTION_TRANSLATION_KEYS: Record<SystemRoleType, TranslationKey> = {
  ACCOUNT_OWNER: keyFromSelector(($) => $.ROLE_SYSTEM_ACCOUNT_OWNER_DESCRIPTION),
  ACCOUNT_ADMINISTRATOR: keyFromSelector(($) => $.ROLE_SYSTEM_ACCOUNT_ADMINISTRATOR_DESCRIPTION),
  USER: keyFromSelector(($) => $.ROLE_SYSTEM_USER_DESCRIPTION),
  FOLDER_MANAGER: keyFromSelector(($) => $.ROLE_SYSTEM_FOLDER_MANAGER_DESCRIPTION),
  FORM_MANAGER: keyFromSelector(($) => $.ROLE_SYSTEM_FORM_MANAGER_DESCRIPTION),
  FORM_USER: keyFromSelector(($) => $.ROLE_SYSTEM_FORM_USER_DESCRIPTION),
};
